import Heading from '@atlaskit/heading';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { Box, Grid, media, Stack, xcss } from '@atlaskit/primitives';
import { useTenantData } from '@dev-agents/tenant-context';
import { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';

import { fg } from '../../../controllers/feature-gates';
import { GITHUB_INSTALL_LINK } from '../../../utils/constants';
import { useUserPermData } from '../../../utils/user-perm-context';
import { Card } from '../common/card';
import { IntegrationsIcon } from '../common/integrations-icon';
import {
	getConfiguredProviders,
	getManageBitbucketLink,
	isBitbucketConnectedOnTenant,
	isGitHubInstalledOnTenant,
} from '../utils/integrations';
import { getGithubLink, getManagementTools } from '../utils/managementTools';

import type { manage_IntegrationsConfigStates$key } from './__generated__/manage_IntegrationsConfigStates.graphql';

const suggestionsStyles = xcss({
	paddingTop: 'space.200',
});

const manageSectionWrapper = xcss({
	paddingTop: 'space.500',
});

const responsiveStyles = xcss({
	[media.above.xxs]: { gridTemplateColumns: 'repeat(1, 1fr)' },
	[media.above.sm]: { gridTemplateColumns: 'repeat(2, 1fr)' },
	[media.above.md]: {
		gridTemplateColumns: 'repeat(3, 1fr)',
	},
});

export const Manage = ({
	integrationsConfigStatesFragment,
}: {
	integrationsConfigStatesFragment: manage_IntegrationsConfigStates$key | undefined | null;
}) => {
	const { jiraAdminPermissions, sitePermissions } = useUserPermData();

	const integrations = useFragment(
		graphql`
			fragment manage_IntegrationsConfigStates on JiraAppConfigStateEdge @relay(plural: true) {
				node @required(action: THROW) {
					id @required(action: THROW)
					appDisplayName
					appIconLink
					config @required(action: THROW) {
						edges @required(action: THROW) {
							node {
								status
								configureLink
							}
						}
					}
				}
			}
		`,
		integrationsConfigStatesFragment,
	);

	const configStates = Object.fromEntries(
		(integrations || []).map((edge) => [
			edge.node.id,
			edge.node.config.edges[0]?.node?.status || 'NOT_CONFIGURED',
		]),
	);

	const configuredProviders = getConfiguredProviders(configStates);

	const { tenantContexts } = useTenantData();
	const { cloudId, orgId, devaiEntitlementId } = tenantContexts[0];

	const getSCMIntegrationsDescription = () => {
		if (configuredProviders.length === 0) {
			return 'No code management tools are connected';
		}

		if (configuredProviders.length === 1) {
			return 'There is 1 code management tool connected';
		}

		return `${configuredProviders.length} code management tools are connected`;
	};

	const managementTools = getManagementTools(orgId, cloudId, devaiEntitlementId, sitePermissions);

	const GithubActionButton = useMemo(() => {
		const isGithubInstalled = isGitHubInstalledOnTenant(configStates);
		if (!fg('rovo-dev-agents-permissions-patch-enabled')) {
			return isGithubInstalled
				? {
						label: 'Manage GitHub',
						icon: LinkExternalIcon,
						href: {
							link: getGithubLink(),
						},
						analyticEvent: {
							action: 'clicked',
							actionSubject: 'link',
							actionSubjectId: 'manage-gh-link',
							source: 'home-page-manage',
						},
					}
				: {
						label: 'Install GitHub for Jira',
						icon: LinkExternalIcon,
						href: {
							link: GITHUB_INSTALL_LINK,
						},
						analyticEvent: {
							action: 'clicked',
							actionSubject: 'link',
							actionSubjectId: 'install-gh4j-link',
							source: 'home-page-manage',
						},
					};
		}
		if (!isGithubInstalled && jiraAdminPermissions.includes('create:app-installation')) {
			return {
				label: 'Install GitHub for Jira',
				icon: LinkExternalIcon,
				href: {
					link: GITHUB_INSTALL_LINK,
				},
				analyticEvent: {
					action: 'clicked',
					actionSubject: 'link',
					actionSubjectId: 'install-gh4j-link',
					source: 'home-page-manage',
				},
			};
		}
		if (
			isGithubInstalled &&
			jiraAdminPermissions.includes('delete:app-installation') &&
			jiraAdminPermissions.includes('update:app-installation')
		) {
			return {
				label: 'Manage GitHub',
				icon: LinkExternalIcon,
				href: {
					link: getGithubLink(),
				},
				analyticEvent: {
					action: 'clicked',
					actionSubject: 'link',
					actionSubjectId: 'manage-gh-link',
					source: 'home-page-manage',
				},
			};
		}
		return undefined;
	}, [jiraAdminPermissions, configStates]);

	const BitbucketActionButton = useMemo(() => {
		const isBitbucketConnected = isBitbucketConnectedOnTenant(configStates);
		if (!fg('rovo-dev-agents-permissions-patch-enabled')) {
			return isBitbucketConnected
				? {
						label: 'Manage Bitbucket',
						icon: LinkExternalIcon,
						href: {
							link: getManageBitbucketLink(),
						},
						analyticEvent: {
							action: 'clicked',
							actionSubject: 'link',
							actionSubjectId: 'manage-bbc',
							source: 'home-page-manage',
						},
					}
				: {
						label: 'Connect Bitbucket',
						icon: LinkExternalIcon,
						href: {
							link: getManageBitbucketLink(),
						},
						analyticEvent: {
							action: 'clicked',
							actionSubject: 'link',
							actionSubjectId: 'connect-bbc',
							source: 'home-page-manage',
						},
					};
		}
		if (!jiraAdminPermissions.includes('manage')) {
			return undefined;
		}
		return isBitbucketConnected
			? {
					label: 'Manage Bitbucket',
					icon: LinkExternalIcon,
					href: {
						link: getManageBitbucketLink(),
					},
					analyticEvent: {
						action: 'clicked',
						actionSubject: 'link',
						actionSubjectId: 'manage-bbc',
						source: 'home-page-manage',
					},
				}
			: {
					label: 'Connect Bitbucket',
					icon: LinkExternalIcon,
					href: {
						link: getManageBitbucketLink(),
					},
					analyticEvent: {
						action: 'clicked',
						actionSubject: 'link',
						actionSubjectId: 'connect-bbc',
						source: 'home-page-manage',
					},
				};
	}, [jiraAdminPermissions, configStates]);

	if (!managementTools && !GithubActionButton && !BitbucketActionButton) {
		// If there are no management tools and no GitHub or Bitbucket actions, don't show the section
		return null;
	}

	return (
		<Box xcss={manageSectionWrapper}>
			<Stack space="space.150">
				<Heading size="xlarge" testId="manage-section-heading">
					Manage Rovo Dev Agents
				</Heading>
			</Stack>
			<Box xcss={suggestionsStyles}>
				<Grid xcss={responsiveStyles} gap="space.200">
					{managementTools &&
						managementTools.map((tool, index) => (
							<Card
								description={tool.description}
								icon={tool.icon}
								primaryAction={tool.primaryAction}
								secondaryAction={tool.secondaryAction}
								title={tool.title}
								key={`tool-${index}`}
							/>
						))}
					{/* For now we are hardcoding this block to show GitHub & Bitbucket.
					No very scalable but the current design won't work for > 2 integrations anyway. */}
					{(GithubActionButton || BitbucketActionButton) && (
						<Card
							description={getSCMIntegrationsDescription()}
							icon={<IntegrationsIcon />}
							primaryAction={GithubActionButton}
							secondaryAction={BitbucketActionButton}
							title="Code management tools"
							uniformActions
						/>
					)}
				</Grid>
			</Box>
		</Box>
	);
};
